import { ElementType, useLayoutEffect, useRef } from "react";
import {
	applyLetterVariant,
	pickVariant,
	SETTLED,
	variantKey,
} from "../lib/letterSets";

const SPACE = "\u00a0";
const GLITCH_EVERY_MS = 3000;
const GLITCH_BURST_MS = 1100;
const GLITCH_TICK_MS = 90;
const FIRST_GLITCH_MS = 500;

const GLITCH_COLORS = [
	"#ea4e2c",
	"#e23722",
	"#00e5ff",
	"#ff2bd6",
	"#39ff14",
	"#ffe600",
	"#4d7cff",
	"#ffffff",
	"#7cf5ff",
	"#ff6b4a",
];

function pickColor(exclude?: string) {
	const pool = exclude
		? GLITCH_COLORS.filter((color) => color !== exclude)
		: GLITCH_COLORS;
	return pool[Math.floor(Math.random() * pool.length)];
}

type GlitchTextProps = {
	text: string;
	lines?: string[];
	as?: ElementType;
	id?: string;
	className?: string;
	stageClassName?: string;
	ariaLabel?: string;
};

export default function GlitchText({
	text,
	lines,
	as: Tag = "span",
	id,
	className = "",
	stageClassName = "",
	ariaLabel,
}: GlitchTextProps) {
	const stageRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<HTMLElement>(null);
	const keysRef = useRef<string[]>([]);
	const glitchTimer = useRef(0);
	const firstTimer = useRef(0);
	const burstTimer = useRef(0);
	const tickTimer = useRef(0);

	const displayLines = lines?.length ? lines : [text];
	const target = displayLines.join("");

	useLayoutEffect(() => {
		const stage = stageRef.current;
		const root = rootRef.current;
		if (!stage || !root) return;

		keysRef.current = target
			.split("")
			.map((letter) => `${letter}:900:normal:normal:normal`);

		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		const charRoots = () =>
			Array.from(root.querySelectorAll<HTMLElement>(".glitch-char"));

		const parts = (charRoot: HTMLElement) => ({
			live: charRoot.querySelector<HTMLElement>(".glitch-char-live"),
			ghostA: charRoot.querySelector<HTMLElement>(".glitch-char-ghost-a"),
			ghostB: charRoot.querySelector<HTMLElement>(".glitch-char-ghost-b"),
		});

		const glyph = (char: string) => (char === " " ? SPACE : char);

		const syncGlyph = (charRoot: HTMLElement, char: string) => {
			const { live, ghostA, ghostB } = parts(charRoot);
			const next = glyph(char);
			if (live) live.textContent = next;
			if (ghostA) ghostA.textContent = next;
			if (ghostB) ghostB.textContent = next;
		};

		const settleLetter = (charRoot: HTMLElement, index: number) => {
			const { live, ghostA, ghostB } = parts(charRoot);
			if (!live) return;
			const char = glyph(target[index]);
			applyLetterVariant(live, { char, ...SETTLED });
			syncGlyph(charRoot, char);
			if (ghostA) ghostA.style.cssText = "";
			if (ghostB) ghostB.style.cssText = "";
			charRoot.classList.remove("is-glitch");
			charRoot.style.removeProperty("--glitch-a");
			charRoot.style.removeProperty("--glitch-b");
			keysRef.current[index] = `${char}:900:normal:normal:normal`;
		};

		const clearLayoutLocks = () => {
			charRoots().forEach((charRoot) => {
				charRoot.style.removeProperty("width");
				charRoot.style.removeProperty("min-width");
				charRoot.style.removeProperty("height");
			});
			stage.style.width = "";
			stage.style.minWidth = "";
			stage.style.height = "";
			stage.style.minHeight = "";
		};

		/** Freeze settled glyph boxes so scramble variants cannot resize the title. */
		const lockLayout = () => {
			charRoots().forEach((charRoot) => {
				charRoot.style.removeProperty("width");
				charRoot.style.removeProperty("min-width");
				charRoot.style.removeProperty("height");
			});
			stage.style.width = "";
			stage.style.minWidth = "";
			stage.style.height = "";
			stage.style.minHeight = "";

			charRoots().forEach((charRoot) => {
				if (charRoot.classList.contains("is-space")) return;
				const rect = charRoot.getBoundingClientRect();
				const w = Math.ceil(rect.width * 1000) / 1000;
				const h = Math.ceil(rect.height * 1000) / 1000;
				if (w > 0) {
					charRoot.style.width = `${w}px`;
					charRoot.style.minWidth = `${w}px`;
				}
				if (h > 0) charRoot.style.height = `${h}px`;
			});

			const settled = root.getBoundingClientRect();
			const stageW = Math.ceil(settled.width);
			const stageH = Math.ceil(settled.height);
			if (stageW > 0) {
				stage.style.width = `${stageW}px`;
				stage.style.minWidth = `${stageW}px`;
			}
			if (stageH > 0) {
				stage.style.height = `${stageH}px`;
				stage.style.minHeight = `${stageH}px`;
			}
		};

		const settleAll = () => {
			charRoots().forEach((charRoot, index) => settleLetter(charRoot, index));
			window.clearInterval(tickTimer.current);
			tickTimer.current = 0;
		};

		const scrambleIndex = (charRoot: HTMLElement, index: number) => {
			const { live, ghostA, ghostB } = parts(charRoot);
			if (!live) return;
			const variant = pickVariant(target[index], keysRef.current[index] || "");
			applyLetterVariant(live, variant);
			syncGlyph(charRoot, variant.char);
			for (const ghost of [ghostA, ghostB]) {
				if (!ghost) continue;
				ghost.style.fontWeight = String(variant.weight);
				ghost.style.fontStyle = variant.style;
				ghost.style.fontVariantCaps = variant.caps;
				ghost.style.fontStretch = variant.stretch;
			}
			keysRef.current[index] = variantKey(variant);
		};

		const startGlitch = () => {
			settleAll();
			lockLayout();
			const nodes = charRoots().filter((node, index) => {
				const char = target[index];
				return Boolean(char && char.trim());
			});
			if (!nodes.length) return;

			const count = Math.max(
				1,
				Math.floor(Math.random() * Math.min(nodes.length, 4)) + 1,
			);

			const pool = nodes.map((_, index) => index);
			for (let i = pool.length - 1; i > 0; i -= 1) {
				const j = Math.floor(Math.random() * (i + 1));
				[pool[i], pool[j]] = [pool[j], pool[i]];
			}

			const activeIndexes = pool.slice(0, Math.min(count, pool.length));
			const activeNodes = activeIndexes.map((index) => nodes[index]);

			activeNodes.forEach((charRoot) => {
				const index = Number(charRoot.dataset.charIndex || "0");
				const colorA = pickColor();
				const colorB = pickColor(colorA);
				charRoot.style.setProperty("--glitch-a", colorA);
				charRoot.style.setProperty("--glitch-b", colorB);
				charRoot.classList.add("is-glitch");
				scrambleIndex(charRoot, index);
			});

			tickTimer.current = window.setInterval(() => {
				activeNodes.forEach((charRoot) => {
					const index = Number(charRoot.dataset.charIndex || "0");
					scrambleIndex(charRoot, index);
				});
			}, GLITCH_TICK_MS);

			burstTimer.current = window.setTimeout(() => {
				settleAll();
				lockLayout();
			}, GLITCH_BURST_MS);
		};

		const stop = () => {
			window.clearTimeout(firstTimer.current);
			window.clearInterval(glitchTimer.current);
			window.clearTimeout(burstTimer.current);
			window.clearInterval(tickTimer.current);
			firstTimer.current = 0;
			glitchTimer.current = 0;
			burstTimer.current = 0;
			tickTimer.current = 0;
			settleAll();
			clearLayoutLocks();
		};

		const start = () => {
			if (reduced) {
				settleAll();
				return;
			}
			stop();
			firstTimer.current = window.setTimeout(() => {
				startGlitch();
				glitchTimer.current = window.setInterval(startGlitch, GLITCH_EVERY_MS);
			}, FIRST_GLITCH_MS);
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) start();
				else stop();
			},
			{ threshold: 0, rootMargin: "0px" },
		);
		observer.observe(stage);
		settleAll();
		lockLayout();

		const onResize = () => {
			settleAll();
			lockLayout();
		};
		window.addEventListener("resize", onResize);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", onResize);
			stop();
		};
	}, [target]);

	let charIndex = 0;

	const renderChar = (index: number) => {
		const char = target[index] === " " ? SPACE : target[index];
		const isSpace = target[index] === " " || target[index] === SPACE;
		return (
			<span
				key={index}
				className={`glitch-char${isSpace ? " is-space" : ""}`}
				data-char-index={index}
			>
				<span
					className="glitch-char-ghost glitch-char-ghost-a"
					aria-hidden="true"
				>
					{char}
				</span>
				<span
					className="glitch-char-ghost glitch-char-ghost-b"
					aria-hidden="true"
				>
					{char}
				</span>
				<span className="glitch-char-live">{char}</span>
			</span>
		);
	};

	return (
		<div
			className={["glitch-stage", stageClassName].filter(Boolean).join(" ")}
			ref={stageRef}
		>
			<Tag
				ref={rootRef}
				id={id}
				className={["glitch-text", className].filter(Boolean).join(" ")}
				aria-label={ariaLabel || text}
			>
				{displayLines.map((line, lineIndex) => (
					<span key={`${line}-${lineIndex}`} className="glitch-line">
						{Array.from({ length: line.length }, () => {
							const index = charIndex;
							charIndex += 1;
							return renderChar(index);
						})}
					</span>
				))}
			</Tag>
		</div>
	);
}
